/**
 * The form-view surface: what can be queried, and what broke generating it.
 *
 * Two globals, following form-data.jsx's pattern:
 *   window.FormViewTab   — a fourth tab on the form detail (the consumer)
 *   window.FormViewStrip — four counts above the form list (the operator)
 *
 * Priority values render with dir="auto" PER ELEMENT. A container marked
 * dir="rtl" would move the punctuation of the LTR parts too.
 */
const { useState, useEffect, useRef } = React;

const fvCls = (...xs) => xs.filter(Boolean).join(' ');
const fvNum = (n) => (n === null || n === undefined ? '—' : n.toLocaleString());

// Both lists carry OBJECTS, not strings: permissive records a dropped
// condition as {condition, alias, proof} and a dropped alias as
// {alias, table, reason}. Rendering one straight into JSX throws React
// error #31 ("objects are not valid as a React child"), which unmounts the
// whole tab and leaves a BLANK page -- the failure this function exists to
// prevent. Strings are still accepted, because nothing guarantees an older
// stored row is not one.
function fvDropLine(d) {
  if (d === null || d === undefined) return '';
  if (typeof d !== 'object') return String(d);
  const what = d.condition || d.alias || d.column || '';
  const owner = d.condition && d.alias ? d.alias : (d.table || '');
  const why = d.proof || d.reason || '';
  return (
    <React.Fragment>
      <span className="mono">{owner ? owner + ' · ' : ''}{String(what)}</span>
      {why && <span className="fv-note"> — {String(why)}</span>}
    </React.Fragment>
  );
}


function FvDropped({ desc, lang }) {
  const aliases = desc.aliases_dropped || [];
  const conds = desc.conditions_dropped || [];
  if (!aliases.length && !conds.length) return null;
  return (
    <details className="card fv-dropped">
      <summary>
        {lang === 'he'
          ? `נשמט מהתצוגה (${aliases.length} טבלאות, ${conds.length} תנאים)`
          : `Dropped from this view — ${aliases.length} table(s), ${conds.length} condition(s)`}
      </summary>
      <p className="fv-note">
        {lang === 'he'
          ? 'שדות אלה אינם קיימים בתצוגה כלל, ואי אפשר לראות זאת מתוך הנתונים.'
          : 'These are absent from the view entirely, and nothing in the data itself reveals that.'}
      </p>
      {aliases.length > 0 && (
        <ul className="fv-list">
          {aliases.map((a, i) => <li key={i} dir="auto">{fvDropLine(a)}</li>)}
        </ul>
      )}
      {conds.length > 0 && (
        <ul className="fv-list">
          {conds.map((c, i) => <li key={i} dir="auto">{fvDropLine(c)}</li>)}
        </ul>
      )}
    </details>
  );
}

// The company list rides on describe_view. It deliberately does NOT come
// from PCMAdapter.getFormEnvs: that endpoint filters extract_batches on
// source == the FORM name, and the mirror has shipped under 'T_<TNAME>'
// sources since 2026-08-27, so it would be empty for essentially every view
// and this pane would never render.
function FvRows({ ename, desc, lang }) {
  const envs = desc.envs || [];
  const [env, setEnv] = useState(envs.length === 1 ? envs[0] : '');
  const [page, setPage] = useState(null);
  const [stack, setStack] = useState([]);
  const [err, setErr] = useState('');
  // One generation counter guards both the initial-load effect and next():
  // whichever request's caller no longer matches the current generation
  // (env/ename changed underneath it) drops its result instead of applying
  // it — otherwise a stale page can land after the company was switched and
  // render one company's rows under another company's heading.
  const genRef = useRef(0);

  useEffect(() => {
    const gen = ++genRef.current;
    setPage(null);
    setStack([]);
    setErr('');
    if (!env) return;
    PCMAdapter.getFormViewRows(ename, { env, limit: 50 })
      .then(d => { if (genRef.current === gen) setPage(d); })
      .catch(e => { if (genRef.current === gen) setErr(String(e.message || e)); });
  }, [ename, env]);

  const next = () => {
    const gen = genRef.current;
    PCMAdapter.getFormViewRows(ename, { env, limit: 50, after: page.next_after })
      .then(d => { if (genRef.current === gen) { setStack(s => [...s, page]); setPage(d); } })
      .catch(e => { if (genRef.current === gen) setErr(String(e.message || e)); });
  };
  const back = () => {
    setPage(stack[stack.length - 1]);
    setStack(s => s.slice(0, -1));
  };

  const titles = {};
  (desc.columns || []).forEach(c => { titles[c.name] = c.title; });

  return (
    <div className="fv-rows">
      <div className="fv-envbar">
        <label>{lang === 'he' ? 'חברה' : 'Company'}</label>
        <select value={env} onChange={e => setEnv(e.target.value)}>
          <option value="">{lang === 'he' ? 'בחרו חברה…' : 'Pick a company…'}</option>
          {envs.map(e => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
      </div>
      {err && <div role="alert" className="error-pane" dir="auto">{err}</div>}
      {page && (
        <div className="card">
          {(page.hidden_columns || []).length > 0 && (
            <p className="fd-note" dir="auto">
              {lang === 'he'
                ? `${page.hidden_columns.length} עמודות אישורים מוסתרות: ${page.hidden_columns.join(', ')}`
                : `${page.hidden_columns.length} credential column(s) hidden: ${page.hidden_columns.join(', ')}`}
            </p>
          )}
          <div className="tbl-wrap" tabIndex={0}>
            <table className="tbl">
              <thead>
                <tr>{page.columns.map(c => (
                  <th key={c}>
                    <span className="mono">{c}</span>
                    {titles[c] && <span className="fv-title" dir="auto"> {titles[c]}</span>}
                  </th>
                ))}</tr>
              </thead>
              <tbody>
                {page.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((v, j) => (
                      <td key={j} dir="auto">
                        {v === null ? '—' : String(v)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="fv-pager">
            <button className="btn btn-sm" disabled={!stack.length} onClick={back} aria-label={lang === 'he' ? 'העמוד הקודם' : 'Previous page'}>‹</button>
            <button className="btn btn-sm" disabled={!page.next_after} onClick={next} aria-label={lang === 'he' ? 'העמוד הבא' : 'Next page'}>›</button>
            {page.paging === 'unavailable' && (
              <span className="fv-note">
                {lang === 'he'
                  ? 'הטופס אינו חושף את מפתח הטבלה שלו, ולכן אין דפדוף — עמוד אחד בלבד.'
                  : 'This form does not publish its base table’s key, so there is no paging — one page only.'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FormViewTab({ ename, lang }) {
  const [desc, setDesc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    setDesc(null); setErr(null);
    PCMAdapter.getFormView(ename)
      .then(d => { if (live) setDesc(d); })
      .catch(e => { if (live) setErr({ status: e.status, message: String(e.message || e) }); });
    return () => { live = false; };
  }, [ename]);

  // Only a 404 means "this form has no view" — a 500, a timeout or a
  // network drop are real failures and must not read as that empty state.
  if (err && err.status === 404) {
    return (
      <div className="card fd-empty" dir="auto">
        {lang === 'he'
          ? 'לא נוצרה תצוגה לטופס זה.'
          : 'No view was generated for this form.'}
      </div>
    );
  }
  if (err) {
    return (
      <div className="card error-pane" dir="auto">
        {lang === 'he' ? 'שגיאה בטעינת התצוגה: ' : 'Error loading the view: '}
        {err.message}
      </div>
    );
  }
  if (!desc) return <div className="card fv-pad">…</div>;

  const head = (
    <div className="fv-head">
      <span className={fvCls('badge', `fv-${desc.status}`)}>{desc.status}</span>
      <span className="mono">
        {lang === 'he' ? 'שורות' : 'rows'} <span dir="ltr">{fvNum(desc.rows_view)} / {fvNum(desc.rows_base)}</span>
      </span>
      <span className="fv-note" dir="ltr">{desc.generated_at}</span>
    </div>
  );

  if (desc.status === 'blocked') {
    return (
      <div className="card fv-pad" dir="auto">
        {head}
        <p>{lang === 'he'
          ? 'התצוגה חסומה עד שהטבלאות הבאות ישוקפו. אין פעולה לבצע — היא תיפתר מעצמה.'
          : 'Blocked until these tables are mirrored. Nothing to do — it clears itself.'}</p>
        <ul className="fv-list mono">
          {desc.tables_missing.map(t => <li key={t}>{t}</li>)}
        </ul>
      </div>
    );
  }
  if (desc.status === 'fanout') {
    return (
      <div className="card fv-pad" dir="auto">
        {head}
        <p>{lang === 'he'
          ? `התצוגה החזירה פי ${desc.ratio ?? '?'} שורות ממספר המפתחות בטבלת הבסיס, ולכן נמחקה.`
          : `This view returned ${desc.ratio ?? '?'}× more rows than its base has keys, so it was dropped.`}</p>
        {/* A refused DROP is the one place the no-ungated-view rule is
            knowingly broken, so say it outright instead of leaving it to
            whatever prose the generator happened to write into detail. */}
        <p className="fv-note">{lang === 'he'
          ? 'אם אובייקט BI תלוי בתצוגה, המחיקה מסורבת והתצוגה נשארת קיימת ב-Postgres — הפירוט למטה נוקב בשם התלות החוסמת. שליפת שורות דרך הדף חסומה בכל מקרה.'
          : 'If a BI object depends on it the DROP is refused and the view is still standing in Postgres — the detail below names the blocking dependent. Rows are refused here either way.'}</p>
        <pre className="fd-detail" dir="auto">{desc.detail}</pre>
      </div>
    );
  }
  if (desc.status === 'error') {
    return (
      <div className="card fv-pad" dir="auto">
        {head}
        <div className="mono">{desc.cause}</div>
        <pre className="fd-detail" dir="auto">{desc.detail}</pre>
      </div>
    );
  }

  return (
    <div className="fv-tab">
      {head}
      <FvRows ename={ename} desc={desc} lang={lang}/>
      <FvDropped desc={desc} lang={lang}/>
      <details className="card fv-sql">
        <summary>{lang === 'he' ? 'SQL' : 'View SQL'}</summary>
        <pre className="fd-detail">{desc.sql_text}</pre>
      </details>
    </div>
  );
}

function FormViewStrip({ lang, onOpenForm }) {
  const [sum, setSum] = useState(null);
  const [open, setOpen] = useState('');

  useEffect(() => {
    let live = true;
    PCMAdapter.getFormViewSummary()
      .then(d => { if (live) setSum(d); })
      .catch(() => { if (live) setSum(null); });
    return () => { live = false; };
  }, []);

  if (!sum) return null;
  const c = sum.counts;
  const chip = (k) => (
    <button key={k}
            className={fvCls('fv-chip', `fv-${k}`, open === k && 'active')}
            onClick={() => setOpen(open === k ? '' : k)}>
      {k} <strong>{fvNum(c[k])}</strong>
    </button>
  );

  return (
    <div className="fv-strip">
      <div className="fv-chips">
        {['ok', 'blocked', 'fanout', 'error'].map(chip)}
      </div>
      {sum.ok_empty > 0 && (
        <div className="fv-note">
          {lang === 'he'
            ? `${sum.ok_empty} מתוך ${sum.counts.ok} תצוגות תקינות מחזירות אפס שורות`
            : `${sum.ok_empty} of ${sum.counts.ok} ok views return zero rows`}
        </div>
      )}
      {open === 'error' && (
        <div className="card">
          {sum.errors.map(g => (
            <div className="fv-group" key={g.cause}>
              <div className="mono"><strong>{g.cause}</strong> × {g.n}</div>
              <div className="fv-enames">
                {g.enames.map(e => (
                  <button key={e} className="btn btn-sm"
                          onClick={() => onOpenForm(e)}>{e}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {open === 'fanout' && (
        <div className="card">
          {sum.fanouts.map(f => (
            <div className="fv-group" key={f.ename}>
              <button className="btn btn-sm"
                      onClick={() => onOpenForm(f.ename)}>{f.ename}</button>
              <span className="mono" dir="ltr"> ×{f.ratio ?? '?'} — {fvNum(f.rows_view)} / {fvNum(f.rows_base)}</span>
            </div>
          ))}
        </div>
      )}
      {(open === 'ok' || open === 'blocked') && (
        <FvList status={open} lang={lang} onOpenForm={onOpenForm}/>
      )}
    </div>
  );
}

function FvList({ status, lang, onOpenForm }) {
  const [q, setQ] = useState('');
  // items accumulates across "load more" pages; total is only known once
  // the first page for the current (q, status) has answered.
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Same generation-counter pattern as FvRows above: the [q, status] effect
  // bumps it on every change, and both that effect's response and
  // loadMore's response apply only if the generation still matches — so a
  // page in flight when the filter changes can't land under the new filter.
  const genRef = useRef(0);

  useEffect(() => {
    const gen = ++genRef.current;
    setItems([]);
    setTotal(null);
    // A loadMore pending across a filter change must not leave the new
    // result's Load more button disabled until that old request settles.
    setLoadingMore(false);
    const t = setTimeout(() => {
      PCMAdapter.getFormViews({ q: q || undefined, status, limit: 100 })
        .then(d => { if (genRef.current === gen) { setItems(d.items); setTotal(d.total); } })
        .catch(() => { if (genRef.current === gen) { setItems([]); setTotal(null); } });
    }, 200);
    return () => clearTimeout(t);
  }, [q, status]);

  const loadMore = () => {
    if (loadingMore) return;
    const gen = genRef.current;
    setLoadingMore(true);
    PCMAdapter.getFormViews({ q: q || undefined, status, limit: 100, offset: items.length })
      .then(d => { if (genRef.current === gen) { setItems(is => [...is, ...d.items]); setTotal(d.total); } })
      .catch(() => {})
      .finally(() => { if (genRef.current === gen) setLoadingMore(false); });
  };

  return (
    <div className="card">
      <input className="fv-search" value={q} placeholder={lang === 'he' ? 'חיפוש…' : 'Search…'}
             onChange={e => setQ(e.target.value)}/>
      {total !== null && (
        <div className="fv-note">
          {items.length} / {total}
        </div>
      )}
      <div className="fv-enames">
        {items.map(i => (
          <button key={i.ename} className="btn btn-sm"
                  onClick={() => onOpenForm(i.ename)}>
            {i.ename}
            {i.status === 'ok' && i.rows_view === 0 ? (
              <span className={fvCls('badge', 'fv-empty')} title={
                lang === 'he'
                  ? 'התצוגה נוצרה ומחזירה אפס שורות'
                  : 'view exists and returns zero rows'}>
                {lang === 'he' ? 'ריק' : 'empty'}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      {total !== null && items.length < total && (
        <button className="btn btn-sm" disabled={loadingMore} onClick={loadMore}>
          {lang === 'he' ? 'עוד' : 'Load more'}
        </button>
      )}
    </div>
  );
}

window.FormViewTab = FormViewTab;
window.FormViewStrip = FormViewStrip;
